import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { adminAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Users, ChartBar, Robot, Envelope, Lightning } from '@phosphor-icons/react';

const AdminPage = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [demoLeads, setDemoLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, leadsRes] = await Promise.all([
        adminAPI.getStats(), 
        adminAPI.getUsers(),
        adminAPI.getDemoLeads()
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setDemoLeads(leadsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-testid="admin-title">
            Admin Dashboard
          </h1>
          <p className="text-lg text-slate-600">Platform overview and user management</p>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <Link 
            to="/admin/operations"
            className="inline-flex items-center gap-2 px-5 py-3 bg-amber-100 text-amber-800 rounded-xl hover:bg-amber-200 transition-colors font-medium"
            data-testid="operations-link"
          >
            <Lightning size={20} weight="duotone" />
            Daily Content Operations
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8" data-testid="admin-total-users">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users size={24} weight="duotone" className="text-blue-600" />
              </div>
              <div>
                <div className="text-sm text-slate-500 uppercase tracking-wider">Total Users</div>
                <div className="text-3xl font-bold text-slate-900">{stats?.total_users || 0}</div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8" data-testid="admin-total-runs">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Robot size={24} weight="duotone" className="text-purple-600" />
              </div>
              <div>
                <div className="text-sm text-slate-500 uppercase tracking-wider">Total Runs</div>
                <div className="text-3xl font-bold text-slate-900">{stats?.total_runs || 0}</div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <ChartBar size={24} weight="duotone" className="text-emerald-600" />
              </div>
              <div>
                <div className="text-sm text-slate-500 uppercase tracking-wider">Avg per User</div>
                <div className="text-3xl font-bold text-slate-900">
                  {stats?.total_users > 0 ? Math.round(stats.total_runs / stats.total_users) : 0}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8" data-testid="admin-demo-leads">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Envelope size={24} weight="duotone" className="text-amber-600" />
              </div>
              <div>
                <div className="text-sm text-slate-500 uppercase tracking-wider">Demo Leads</div>
                <div className="text-3xl font-bold text-slate-900">{stats?.total_demo_leads || 0}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Runs by Bot */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 mb-12">
          <h2 className="text-xl font-semibold text-slate-900 mb-6">Runs by Bot Type</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats?.runs_by_bot &&
              Object.entries(stats.runs_by_bot).map(([bot, count]) => (
                <div key={bot} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="text-2xl font-bold text-slate-900">{count}</div>
                  <div className="text-sm text-slate-600 capitalize">{bot.replace('_', ' ')}</div>
                </div>
              ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setActiveTab('users')}
            data-testid="admin-tab-users"
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'users'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Users
          </button>
          <button
            onClick={() => setActiveTab('leads')}
            data-testid="admin-tab-leads"
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'leads'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Demo Leads
          </button>
        </div>

        {/* Users Table */}
        {activeTab === 'users' && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-8 border-b border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900">All Users</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-users-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Total Runs
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Joined
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{user.name}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{user.email}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium uppercase">
                        {user.plan_tier}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{user.total_runs}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Demo Leads Table */}
        {activeTab === 'leads' && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-8 border-b border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900">Demo Leads</h2>
            <p className="text-sm text-slate-500 mt-1">Users who signed up for the demo</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-demo-leads-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {demoLeads.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                      No demo leads yet
                    </td>
                  </tr>
                ) : (
                  demoLeads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{lead.first_name}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{lead.email}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                        {lead.source}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;